import React, {useMemo} from 'react';
import {ProgressBar} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {readablePriceHelper} from '../../Helpers/readablePrice';
import {ReadableSizeHelper} from '../../Helpers/readableSize';
import {seconds2durationHelper} from '../../Helpers/seconds2duration';
import {useGetTelephonyUsage} from '../../Hooks/TelephonieManager';

interface ConsommationsDetailleesResumeInterface {
  selectedMonth: string;
  selectedOrder: string;
  serviceId?: string;
}

interface ResumeEncoursSummary {
  TotalVoice: number;
  TotalData: number;
  TotalMessage: number;
  TotalPrice: number;
  Quota: number;
}

const ConsommationsDetailleesResume = ({
  selectedMonth,
  selectedOrder,
  serviceId,
}: ConsommationsDetailleesResumeInterface) => {
  const intl = useIntl();
  const ReadableSizeHelperInstance = useMemo(() => new ReadableSizeHelper(), []);
  const readablePriceHelperInstance = useMemo(() => new readablePriceHelper(), []);
  const seconds2durationHelperInstance = useMemo(() => new seconds2durationHelper(), []);

  const {data} = useGetTelephonyUsage({
    month: selectedMonth,
    order: selectedOrder,
    info: 'destination',
    summary: true,
    serviceId,
  });

  const resumeEncoursSummary = useMemo<ResumeEncoursSummary>(() => {
    let totalVoice = 0;
    let totalData = 0;
    let totalMessage = 0;
    let totalPrice = 0;
    let quota = data?.quota.quotaOverload ?? 0;

    if ((data?.calls ?? []).length > 0) {
      data?.calls.forEach((appel: any) => {
        totalPrice += parseFloat(appel.price ?? 0);
        totalVoice += parseFloat(appel.time ?? 0);
        if (appel.CellularType === 'Data') {
          totalData += parseFloat(appel.data ?? 0);
        }
        if (appel.CellularType === 'Message') {
          totalMessage += parseFloat(appel.message ?? 0);
        }
      });
    }

    return {
      TotalVoice: totalVoice,
      TotalData: totalData,
      TotalMessage: totalMessage,
      TotalPrice: totalPrice,
      Quota: quota,
    };
  }, [data]);

  const quotaPercent =
    resumeEncoursSummary.Quota > 0
      ? (resumeEncoursSummary.TotalPrice / resumeEncoursSummary.Quota) * 100
      : 0;
  const quotaVariant = quotaPercent >= 100 ? 'danger' : quotaPercent >= 80 ? 'warning' : 'success';

  return (
    <>
      <p className="mb-2">
        {intl.formatMessage({id: 'TELEPHONIE.CONSO.RESUME.INTRO'})}{' '}
        <strong>
          {readablePriceHelperInstance.readablePrice(resumeEncoursSummary.TotalPrice)} €
        </strong>{' '}
        <span className="text-muted">
          {intl.formatMessage(
            {id: 'TELEPHONIE.CONSO.RESUME.DETAIL'},
            {
              voice: seconds2durationHelperInstance.seconds2duration(
                resumeEncoursSummary.TotalVoice
              ),
              data: ReadableSizeHelperInstance.readableSize(resumeEncoursSummary.TotalData),
              messages: ReadableSizeHelperInstance.readableSize(resumeEncoursSummary.TotalMessage),
            }
          )}
        </span>
      </p>

      {resumeEncoursSummary.Quota > 0 && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-baseline mb-2">
            <span className="fw-semibold text-gray-700">
              {intl.formatMessage({id: 'TELEPHONIE.CONSO.RESUME.QUOTA'})}
            </span>
            <span className="text-gray-600">
              <span className={`fw-bold text-${quotaVariant}`}>
                {readablePriceHelperInstance.readablePrice(resumeEncoursSummary.TotalPrice)} €
              </span>{' '}
              / {readablePriceHelperInstance.readablePrice(resumeEncoursSummary.Quota)} €
            </span>
          </div>
          <ProgressBar
            now={Math.min(quotaPercent, 100)}
            variant={quotaVariant}
            style={{height: '8px'}}
            aria-label={intl.formatMessage({id: 'TELEPHONIE.CONSO.RESUME.QUOTA'})}
          />
          <div className="d-flex justify-content-between fs-7 mt-2">
            <span className={`fw-semibold text-${quotaVariant}`}>
              {intl.formatNumber(quotaPercent / 100, {style: 'percent', maximumFractionDigits: 0})}
            </span>
            <span className={quotaPercent >= 100 ? 'fw-semibold text-danger' : 'text-muted'}>
              {intl.formatMessage(
                {
                  id:
                    quotaPercent >= 100
                      ? 'TELEPHONIE.CONSO.RESUME.QUOTA_EXCEEDED'
                      : 'TELEPHONIE.CONSO.RESUME.QUOTA_REMAINING',
                },
                {
                  amount: readablePriceHelperInstance.readablePrice(
                    Math.abs(resumeEncoursSummary.Quota - resumeEncoursSummary.TotalPrice)
                  ),
                }
              )}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsommationsDetailleesResume;
